"use client";

import * as React from "react";
import { cn } from "@gerama/utils";

/**
 * A minimal anchored popover: closes on outside click and Escape. Used for the
 * header bell and the browse-categories panel, where a dropdown *menu* (roving
 * focus, closes on every item) is the wrong shape.
 */
export function Popover({
  trigger,
  children,
  className,
  align = "end",
}: {
  trigger: (props: {
    open: boolean;
    toggle: () => void;
    ref: React.Ref<HTMLButtonElement>;
  }) => React.ReactNode;
  children: (close: () => void) => React.ReactNode;
  className?: string;
  align?: "start" | "end";
}) {
  const [open, setOpen] = React.useState(false);
  const root = React.useRef<HTMLDivElement>(null);
  const close = React.useCallback(() => setOpen(false), []);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      {trigger({ open, toggle: () => setOpen((v) => !v), ref: () => undefined })}
      {open && (
        <div
          className={cn(
            "bg-popover text-popover-foreground border-border absolute top-full z-50 mt-2 rounded-xl border shadow-2xl",
            align === "end" ? "end-0" : "start-0",
            className,
          )}
        >
          {children(close)}
        </div>
      )}
    </div>
  );
}
